import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export function AdminCard({
  title,
  subtitle,
  actions,
  children,
  className = '',
  padded = true,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={`rounded-2xl border border-background-200 bg-background-50 overflow-hidden ${className}`}
    >
      {title ? (
        <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-background-200">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-foreground-950 font-heading">{title}</h2>
            {subtitle ? <p className="mt-0.5 text-[12px] text-foreground-500">{subtitle}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  icon: string;
  tone?: 'default' | 'primary' | 'accent';
}) {
  const toneMap = {
    default: 'bg-background-100 text-foreground-700 border-background-200',
    primary: 'bg-primary-600 text-background-50 border-primary-700',
    accent: 'bg-accent-500 text-background-50 border-accent-600',
  } as const;

  return (
    <div className={`rounded-2xl border p-4 ${toneMap[tone]}`}>
      <div className="flex items-start justify-between gap-2">
        <p
          className={`text-[11px] font-semibold uppercase tracking-[0.1em] font-label ${
            tone === 'default' ? 'text-foreground-500' : 'opacity-80'
          }`}
        >
          {label}
        </p>
        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-background-50/15 shrink-0">
          <i className={`${icon} text-sm`} aria-hidden="true" />
        </span>
      </div>
      <p className="mt-2 text-[24px] leading-none font-semibold tabular font-heading">{value}</p>
      {hint ? (
        <p className={`mt-1.5 text-[11.5px] ${tone === 'default' ? 'text-foreground-500' : 'opacity-75'}`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 py-8 overflow-y-auto">
      <button
        type="button"
        aria-label={t('admin.common.close')}
        onClick={onClose}
        className="fixed inset-0 bg-foreground-950/45 backdrop-blur-sm cursor-default"
      />
      <div
        className={`relative w-full ${width} rounded-2xl border border-background-200 bg-background-50 animate-fade-up`}
      >
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-background-200">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-foreground-950 font-heading">{title}</h2>
            {subtitle ? <p className="mt-1 text-[12.5px] text-foreground-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('admin.common.close')}
            className="flex items-center justify-center w-8 h-8 rounded-full text-foreground-400 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg" aria-hidden="true" />
          </button>
        </header>
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 px-5 py-3.5 border-t border-background-200 bg-background-100">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-2.5 py-14 text-[13px] text-foreground-500">
      <span className="flex items-center justify-center w-4 h-4">
        <i className="ri-loader-4-line animate-spin text-base text-primary-600" aria-hidden="true" />
      </span>
      {label ?? t('admin.common.loading')}
    </div>
  );
}

export function EmptyBlock({ label, icon = 'ri-inbox-line' }: { label: string; icon?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-background-100 text-foreground-400">
        <i className={`${icon} text-xl`} aria-hidden="true" />
      </span>
      <p className="text-[12.5px] text-foreground-500">{label}</p>
    </div>
  );
}

export function ErrorBlock({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <span className="flex items-center justify-center w-11 h-11 rounded-full bg-accent-50 text-accent-600">
        <i className="ri-error-warning-line text-xl" aria-hidden="true" />
      </span>
      <p className="text-[13px] text-foreground-600">{t('admin.common.error')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-background-300 text-[12.5px] font-semibold text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
      >
        <span className="flex items-center justify-center w-3.5 h-3.5">
          <i className="ri-refresh-line text-[13px]" aria-hidden="true" />
        </span>
        {t('admin.common.retry')}
      </button>
    </div>
  );
}

export function TableHeaderCell({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-500 font-label ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

export function TableCell({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <td
      className={`px-4 py-3 text-[13px] text-foreground-800 align-middle ${
        align === 'right' ? 'text-right tabular' : ''
      }`}
    >
      {children}
    </td>
  );
}